/**
 * v109.9.13 — Travel · Approval tab (per-user pending step-approvals)
 * ------------------------------------------------------------------
 * Shows every advance where the CURRENT user is listed as a required
 * approver and their slot is still "pending". Approve/Reject inline.
 *
 * Visible to any user with at least one pending approval slot; falls
 * back to a friendly empty state otherwise.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { CheckCircle2, XCircle, ShieldCheck, Wallet } from "lucide-react";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import { useDataChange } from "../lib/events";

function money(v) {
  return `₹ ${Number(v || 0).toLocaleString("en-IN")}`;
}

export default function TravelApprovalQueueTab() {
  const { user } = useAuth();
  const [viewMode] = useGlobalViewMode();
  const uid = String(user?.id || "");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [details, setDetails] = useState(null); // row currently open in the DetailsDialog
  const [dialog, setDialog] = useState(null); // { row, action }
  const [remark, setRemark] = useState("");
  const [unsettled, setUnsettled] = useState(null); // {employee_id: {total_advance, total_settled, net}}

  // v109.9.113 — Approver-facing "unsettled advance" info per submitter.
  // Loaded once so we can show each employee's outstanding balance beside
  // their request.
  const loadUnsettled = async () => {
    try {
      const { data } = await api.get("/travel-advances/unsettled-summary?scope=all");
      const by = {};
      (Array.isArray(data) ? data : []).forEach((u) => {
        by[u.employee_id] = u;
      });
      setUnsettled(by);
    } catch { /* ignore — Access User w/o scope=all just won't see the chip */ }
  };

  const load = async () => {
    setBusy(true);
    try {
      const [advRes, setRes] = await Promise.all([
        api.get("/travel-advances/my-approval-queue").catch(() => ({ data: [] })),
        api.get("/travel-settlements?scope=approver").catch(() => ({ data: [] })),
      ]);
      const advances = (Array.isArray(advRes.data) ? advRes.data : []).map((r) => ({
        ...r,
        _kind: "advance",
        _label: "Travel Advance",
        _amount: Number(r.advance_amount || 0),
        _employee: r.employee_name || r.created_by_name || "",
      }));
      // v109.9.108 — Merge Travel Settlements where I'm the current approver
      // (Assigned-By / Reporting Manager). Show only rows whose overall status
      // is still open (Submitted / AssignerApproved / ManagerApproved).
      // v109.9.173 — Account Admins no longer see settlement rows here. The
      // accounts stage is handled from the Accounts Tab. Filter out rows
      // where the CURRENT pending stage is `accounts`.
      const settlements = (Array.isArray(setRes.data) ? setRes.data : [])
        .filter((r) => ["Submitted", "AssignerApproved", "ManagerApproved"].includes(r.status))
        .filter((r) => {
          const currentPending = (r.approvals || []).find((a) => a.status === "pending");
          return currentPending && currentPending.stage !== "accounts";
        })
        .map((r) => ({
          ...r,
          _kind: "settlement",
          _label: "Travel Settlement",
          _amount: Number(r.claim_amount || 0),
          _employee: r.created_by_name || "",
        }));
      // Sort merged list newest-first for consistency.
      const merged = [...advances, ...settlements].sort((a, b) => {
        const ta = new Date(a.submitted_at || a.created_at || 0).getTime();
        const tb = new Date(b.submitted_at || b.created_at || 0).getTime();
        return tb - ta;
      });
      setRows(merged);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); loadUnsettled(); }, []);

  // v109.9.179 — Refresh queue when any Travel record is mutated elsewhere.
  useDataChange(["travel:updated"], () => { load(); loadUnsettled(); });

  const submit = async () => {
    if (!dialog) return;
    if (dialog.action === "reject" && !remark.trim()) {
      toast.error("Rejection reason is mandatory");
      return;
    }
    try {
      const base = dialog.row._kind === "settlement" ? "/travel-settlements" : "/travel-advances";
      await api.post(`${base}/${dialog.row.id}/step-${dialog.action}`, { remark: remark.trim() });
      toast.success(dialog.action === "approve" ? "Step approved" : `${dialog.row._label} rejected`);
      setDialog(null); setRemark("");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const totals = useMemo(() => ({
    count: rows.length,
    amount: rows.reduce((s, r) => s + Number(r._amount || 0), 0),
    advCount: rows.filter((r) => r._kind === "advance").length,
    setCount: rows.filter((r) => r._kind === "settlement").length,
  }), [rows]);

  return (
    <div className="space-y-4" data-testid="travel-approval-tab">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <SummaryCard tone="amber"  label="Awaiting Your Approval" value={totals.count} icon={<ShieldCheck size={13} />} />
        <SummaryCard tone="indigo" label="Total Amount Pending"  value={money(totals.amount)} icon={<ShieldCheck size={13} />} />
        <SummaryCard tone="sky"    label="Travel Advances"       value={totals.advCount} icon={<ShieldCheck size={13} />} />
        <SummaryCard tone="violet" label="Travel Settlements"    value={totals.setCount} icon={<ShieldCheck size={13} />} />
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/40 p-10 text-center text-slate-500" data-testid="travel-approval-empty">
          <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <div className="text-[13px] font-semibold">
            {busy ? "Loading…" : "You have no pending travel approvals."}
          </div>
          <div className="text-[11.5px] mt-1">You&apos;ll see travel advance requests and settlement submissions here when an employee sends one your way for sign-off.</div>
        </div>
      ) : viewMode === "card" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="travel-approval-cards">
          {rows.map((r) => {
            const mySlot = (r.approvals || []).find((a) => a.user_id === uid);
            const isSet = r._kind === "settlement";
            return (
              <div key={r.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`app-card-${r.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-slate-900 dark:text-white truncate">{r._employee}</div>
                    <div className="text-[10.5px] text-slate-500 truncate">{r.employee_code || ""}{isSet && r.settlement_code ? ` · ${r.settlement_code}` : ""}</div>
                  </div>
                  <div className="flex flex-col gap-1 items-end shrink-0">
                    <Badge className={`text-[9.5px] ${isSet ? "bg-violet-100 text-violet-800 border-violet-200" : "bg-sky-100 text-sky-800 border-sky-200"}`}>
                      {r._label}
                    </Badge>
                    <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200">
                      {mySlot?.role_tag === "reporting_manager" || mySlot?.stage === "reporting_manager" ? "Reporting Mgr" :
                        mySlot?.role_tag === "pool" ? "Pool Approver" :
                        mySlot?.stage === "assigned_by" ? "Assigned-By" :
                        mySlot?.stage === "accounts" ? "Accounts" : "Approver"}
                    </Badge>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                  <div className="col-span-2">
                    <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Purpose</div>
                    <div className="text-slate-800 line-clamp-2">
                      {isSet
                        ? (r.kind === "local" ? `Local · ${r.month || ""} ${r.year || ""}` : (r.purpose_of_travel || "—"))
                        : (<>{r.travel_purpose}{r.bypass_crm ? <Badge className="ml-1.5 bg-rose-100 text-rose-700 text-[9px]">CRM Bypass</Badge> : null}</>)
                      }
                    </div>
                  </div>
                  {!isSet && (
                    <>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">From</div><div className="text-slate-700 text-[10.5px]">{(r.from_datetime || r.from_date || "").replace("T"," ").slice(0,16)}</div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">To</div><div className="text-slate-700 text-[10.5px]">{(r.to_datetime || r.to_date || "").replace("T"," ").slice(0,16)}</div></div>
                    </>
                  )}
                  {isSet && (
                    <>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Kind</div><div className="text-slate-700"><Badge variant="outline" className="text-[10px]">{r.kind}</Badge></div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Status</div><div className="text-slate-700"><Badge variant="outline" className="text-[10px]">{r.status}</Badge></div></div>
                    </>
                  )}
                  <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Amount</div><div className="mono font-semibold">{money(r._amount)}</div></div>
                  {!isSet && <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Flow</div><div><Badge variant="outline" className="text-[10px]">{r.approval_type || "—"}</Badge></div></div>}
                </div>
                <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <Button size="sm" variant="outline" onClick={() => setDetails(r)} className="h-7 text-[10.5px]" data-testid={`app-open-card-${r.id}`}>
                    View Details
                  </Button>
                  <Button size="sm" onClick={() => { setDialog({ row: r, action: "approve" }); setRemark(""); }} className="h-7 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`app-approve-card-${r.id}`}>
                    <CheckCircle2 size={10} className="mr-1" /> Approve
                  </Button>
                  <Button size="sm" onClick={() => { setDialog({ row: r, action: "reject" }); setRemark(""); }} className="h-7 text-[10.5px] bg-rose-600 hover:bg-rose-700 text-white" data-testid={`app-reject-card-${r.id}`}>
                    <XCircle size={10} className="mr-1" /> Reject
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
          <table className="w-full text-[12.5px]" data-testid="travel-approval-table">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10.5px] uppercase tracking-wider text-slate-600 dark:text-slate-300">
              <tr>
                <th className="p-2.5 text-left">Employee</th>
                <th className="p-2.5 text-left">Type</th>
                <th className="p-2.5 text-left">Purpose / Trip</th>
                <th className="p-2.5 text-right">Amount</th>
                <th className="p-2.5 text-center">Status</th>
                <th className="p-2.5 text-center">My Role</th>
                <th className="p-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const mySlot = (r.approvals || []).find((a) => a.user_id === uid);
                const isSet = r._kind === "settlement";
                return (
                  <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/60 dark:hover:bg-slate-800/40 cursor-pointer" onClick={() => setDetails(r)} data-testid={`app-row-${r.id}`}>
                    <td className="p-2.5">
                      <div className="font-semibold">{r._employee}</div>
                      <div className="text-[10.5px] text-slate-500">{r.employee_code || ""}{isSet && r.settlement_code ? ` · ${r.settlement_code}` : ""}</div>
                    </td>
                    <td className="p-2.5">
                      <Badge className={`text-[9.5px] ${isSet ? "bg-violet-100 text-violet-800 border-violet-200" : "bg-sky-100 text-sky-800 border-sky-200"}`}>
                        {r._label}
                      </Badge>
                    </td>
                    <td className="p-2.5">
                      {isSet
                        ? (r.kind === "local" ? `Local · ${r.month || ""} ${r.year || ""}` : (r.purpose_of_travel || "—"))
                        : (<>{r.travel_purpose}{r.bypass_crm ? <Badge className="ml-1.5 bg-rose-100 text-rose-700 border-rose-200 text-[9.5px]">CRM Bypass</Badge> : null}</>)
                      }
                      {!isSet && (
                        <div className="text-[10.5px] text-slate-500 mt-0.5">
                          {(r.from_datetime || r.from_date || "").replace("T"," ").slice(0,16)} → {(r.to_datetime || r.to_date || "").replace("T"," ").slice(0,16)}
                        </div>
                      )}
                    </td>
                    <td className="p-2.5 text-right font-mono font-semibold">{money(r._amount)}</td>
                    <td className="p-2.5 text-center">
                      <Badge variant="outline" className="text-[10px]">{isSet ? r.status : (r.approval_type || "—")}</Badge>
                    </td>
                    <td className="p-2.5 text-center">
                      <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200">
                        {mySlot?.role_tag === "reporting_manager" || mySlot?.stage === "reporting_manager" ? "Reporting Mgr" :
                          mySlot?.role_tag === "pool" ? "Pool Approver" :
                          mySlot?.stage === "assigned_by" ? "Assigned-By" :
                          mySlot?.stage === "accounts" ? "Accounts" : "Approver"}
                      </Badge>
                    </td>
                    <td className="p-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => setDetails(r)} className="h-7 text-[10.5px]" data-testid={`app-open-${r.id}`}>
                          View
                        </Button>
                        <Button size="sm" onClick={() => { setDialog({ row: r, action: "approve" }); setRemark(""); }} className="h-7 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`app-approve-${r.id}`}>
                          <CheckCircle2 size={10} className="mr-1" /> Approve
                        </Button>
                        <Button size="sm" onClick={() => { setDialog({ row: r, action: "reject" }); setRemark(""); }} className="h-7 text-[10.5px] bg-rose-600 hover:bg-rose-700 text-white" data-testid={`app-reject-${r.id}`}>
                          <XCircle size={10} className="mr-1" /> Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
                    {/* v109.9.113 — Details now open in a modal — see DetailsDialog below. */}
            </tbody>
          </table>
        </div>
      )}

      {/* v109.9.113 — Details banner: opens on row / View click, shows every
          filled field plus Approve / Reject shortcuts. */}
      {details && (
        <DetailsDialog
          row={details}
          unsettledMap={unsettled}
          onClose={() => setDetails(null)}
          onApprove={() => { setDialog({ row: details, action: "approve" }); setRemark(""); setDetails(null); }}
          onReject={() => { setDialog({ row: details, action: "reject" }); setRemark(""); setDetails(null); }}
        />
      )}

      {/* Approve/Reject dialog */}
      <Dialog open={!!dialog} onOpenChange={(v) => { if (!v) { setDialog(null); setRemark(""); } }}>
        <DialogContent className="max-w-md" data-testid="app-step-dialog">
          <DialogHeader>
            <DialogTitle className="heading">
              {dialog?.action === "approve" ? `Approve this step` : `Reject ${dialog?.row?._label || "request"} — reason required`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-[12px] text-slate-600 dark:text-slate-300">
              {dialog?.row?._label} for <b>{dialog?.row?._employee}</b> · {money(dialog?.row?._amount)}
              {dialog?.row?._kind === "settlement" && dialog?.row?.settlement_code && (
                <span className="ml-2 mono text-indigo-700">· {dialog.row.settlement_code}</span>
              )}
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                Remark {dialog?.action === "reject" ? "*" : "(optional)"}
              </Label>
              <Textarea
                rows={3}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder={dialog?.action === "reject" ? "Why is this being rejected?" : "Optional note"}
                className="mt-1.5"
                data-testid="app-step-remark"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDialog(null); setRemark(""); }}>Cancel</Button>
            <Button
              onClick={submit}
              className={dialog?.action === "reject" ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
              data-testid="app-step-submit"
            >
              {dialog?.action === "approve" ? "Confirm Approval" : "Confirm Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kv({ k, v, full }) {
  return (
    <div className={full ? "md:col-span-4" : ""}>
      <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">{k}</div>
      <div className="text-[12px] text-slate-800 dark:text-slate-200 mt-0.5 whitespace-pre-wrap break-words">{v || "—"}</div>
    </div>
  );
}

function SummaryCard({ tone, label, value, icon }) {
  const toneCls = {
    amber:   "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-200",
    indigo:  "border-indigo-300 bg-indigo-50 text-indigo-900 dark:bg-indigo-950/40 dark:border-indigo-800 dark:text-indigo-200",
    sky:     "border-sky-300 bg-sky-50 text-sky-900 dark:bg-sky-950/40 dark:border-sky-800 dark:text-sky-200",
    violet:  "border-violet-300 bg-violet-50 text-violet-900 dark:bg-violet-950/40 dark:border-violet-800 dark:text-violet-200",
  }[tone];
  return (
    <div className={`rounded-lg border p-2.5 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5 opacity-80">
        {icon} {label}
      </div>
      <div className="text-[16px] font-bold mt-0.5">{value}</div>
    </div>
  );
}

/* =========================================================================
 * v109.9.113 — DetailsDialog: opens when an approver clicks a queue row.
 * Shows every filled field for the request (advance or settlement) + the
 * submitter's outstanding unsettled-advance balance + Approve / Reject
 * shortcuts.
 * =======================================================================*/
/* v109.9.114 — Exported so the top-bar Approval Requests panel can reuse
   the same details banner. */
export function DetailsDialog({ row, unsettledMap, onClose, onApprove, onReject }) {
  const isSet = row._kind === "settlement";
  const submitterId = row.created_by_id || row.employee_id || "";
  const un = unsettledMap && submitterId ? unsettledMap[submitterId] : null;
  const netUnsettled = un ? (Number(un.total_advance || 0) - Number(un.total_settled || 0)) : null;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="app-details-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">
            <Badge className={`text-[10.5px] ${isSet ? "bg-violet-100 text-violet-800 border-violet-200" : "bg-sky-100 text-sky-800 border-sky-200"}`}>
              {row._label}
            </Badge>
            <span>{row._employee}</span>
            {isSet && row.settlement_code && <span className="mono text-[12px] text-indigo-700">· {row.settlement_code}</span>}
            <span className="ml-auto mono font-bold text-[14px] text-slate-900 dark:text-slate-100">{money(row._amount)}</span>
          </DialogTitle>
          <p className="text-[11.5px] text-slate-500">
            Submitted {(row.submitted_at || row.created_at || "").toString().replace("T", " ").slice(0, 16) || "—"}
          </p>
        </DialogHeader>

        {/* Unsettled advance chip — always show if we know the value */}
        {netUnsettled !== null && (
          <div className={`rounded-lg border p-2.5 my-2 flex items-center gap-2 text-[12px] ${
            netUnsettled > 0
              ? "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/30"
              : "border-emerald-300 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30"
          }`}>
            <Wallet size={14} />
            <span>
              Submitter&apos;s outstanding advance (not yet settled): <b>{money(netUnsettled)}</b>
              {un?.total_advance ? <span className="ml-2 text-slate-500">Total advance so far {money(un.total_advance)} · Settled {money(un.total_settled)}</span> : null}
            </span>
          </div>
        )}

        <div className="space-y-3">
          {isSet ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
              <Kv k="Settlement Code" v={row.settlement_code || "—"} />
              <Kv k="Kind" v={row.kind || "—"} />
              <Kv k="Employee" v={row.created_by_name || "—"} />
              <Kv k="Employee Code" v={row.employee_code || "—"} />
              <Kv k="Reporting Mgr" v={row.reporting_manager_name || "—"} />
              {row.assigned_by_name ? <Kv k="Assigned By" v={row.assigned_by_name} /> : null}
              <Kv k="Sender Email" v={row.sender_email || "—"} />
              <Kv k="Email Sent" v={row.email_sent ? "Yes" : "No"} />
              {row.kind === "local" ? <Kv k="Month" v={`${row.month || "—"} ${row.year || ""}`.trim()} /> : null}
              {row.kind === "tour" ? <Kv k="Purpose" v={row.purpose_of_travel || "—"} full /> : null}
              {row.client_names?.length ? <Kv k="Clients" v={row.client_names.join(", ")} full /> : null}
              <Kv k="Claim Amount" v={money(row._amount)} />
              <Kv k="Status" v={row.status || "—"} />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
              <Kv k="Employee" v={row.employee_name || row.created_by_name || "—"} />
              <Kv k="Employee Code" v={row.employee_code || "—"} />
              <Kv k="Reporting Mgr" v={row.reporting_manager_name || "—"} />
              <Kv k="Approval Flow" v={row.approval_type || "—"} />
              <Kv k="Purpose" v={row.travel_purpose || "—"} />
              {row.purpose_other ? <Kv k="Purpose (Other)" v={row.purpose_other} /> : null}
              <Kv k="From" v={(row.from_datetime || row.from_date || "—").toString().replace("T", " ").slice(0, 16)} />
              <Kv k="To" v={(row.to_datetime || row.to_date || "—").toString().replace("T", " ").slice(0, 16)} />
              <Kv k="Days" v={row.days_count || 0} />
              <Kv k="Destination" v={row.destination || "—"} />
              <Kv k="CRM Client(s)" v={row.client_names?.length ? row.client_names.join(", ") : (row.client_name || (row.bypass_crm ? "— (Bypass)" : "—"))} full />
              <Kv k="CRM Bypass" v={row.bypass_crm ? "Yes" : "No"} />
              {row.transport_modes?.length ? <Kv k="Transport" v={row.transport_modes.join(", ")} /> : null}
              {row.kms ? <Kv k="KMs" v={row.kms} /> : null}
              {row.fuel_cost ? <Kv k="Fuel" v={money(row.fuel_cost)} /> : null}
              {row.toll_cost ? <Kv k="Toll" v={money(row.toll_cost)} /> : null}
              {row.public_transport_cost ? <Kv k="Public Transport" v={money(row.public_transport_cost)} /> : null}
              {row.lodging_cost ? <Kv k="Lodging" v={money(row.lodging_cost)} /> : null}
              {row.food_cost ? <Kv k="Food" v={money(row.food_cost)} /> : null}
              <Kv k="Total Breakdown" v={money(row.total_breakdown_cost)} />
              <Kv k="Advance Amount" v={money(row.advance_amount)} />
              <Kv k="Created At" v={(row.created_at || "").toString().replace("T", " ").slice(0, 16) || "—"} />
              {row.remark ? <Kv k="Employee Remark" v={row.remark} full /> : null}
            </div>
          )}

          {isSet && row.kind === "tour" && row.section_totals ? (
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Section Totals</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px]">
                <Kv k="S1 · Inter-State" v={money(row.section_totals.s1)} />
                <Kv k="S2 · Local Conv" v={money(row.section_totals.s2)} />
                <Kv k="S3 · DA / Food" v={money(row.section_totals.s3)} />
                <Kv k="S4 · Lodging" v={money(row.section_totals.s4)} />
                <Kv k="S5 · Misc" v={money(row.section_totals.s5)} />
              </div>
            </div>
          ) : null}
          {isSet && row.kind === "local" && row.local_entries?.length ? (
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Local Entries ({row.local_entries.length})</div>
              <div className="space-y-1">
                {row.local_entries.map((e, i) => (
                  <div key={i} className="text-[11px] flex items-center justify-between border-b border-slate-100 dark:border-slate-800 py-1 last:border-b-0">
                    <span>{e.date} · {e.start_location} → {e.end_location}</span>
                    <span className="mono font-semibold">{money(e.day_total)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {isSet && row.local_misc_items?.length ? (
            <div className="rounded-md border border-amber-200 dark:border-amber-700 p-2 bg-amber-50/40 dark:bg-amber-950/20">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-800 dark:text-amber-300 mb-1">Miscellaneous ({row.local_misc_items.length})</div>
              <div className="space-y-1">
                {row.local_misc_items.map((m, i) => (
                  <div key={i} className="text-[11px] flex items-center justify-between">
                    <span>{m.date} · {m.expense_type} — {m.reason}</span>
                    <span className="mono font-semibold">{money(m.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Approval chain — approver name + timestamp of action */}
          <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
              Approval Chain
            </div>
            <div className="space-y-1.5">
              {(row.approvals || []).map((a, i) => {
                const at = a.at ? new Date(a.at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true }) : "";
                const label = a.name || (a.stage === "assigned_by" ? "Assigned By" : a.stage === "reporting_manager" ? "Reporting Manager" : a.stage === "accounts" ? "Accounts Team" : a.stage);
                return (
                  <div
                    key={i}
                    className={`flex items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-[11.5px] ${
                      a.status === "approved" ? "border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20" :
                      a.status === "rejected" ? "border-rose-200 bg-rose-50/60 dark:bg-rose-950/20" :
                      "border-amber-200 bg-amber-50/60 dark:bg-amber-950/20"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-800 dark:text-slate-100 truncate">
                        {label}
                      </div>
                      {a.remark ? (
                        <div className="text-[10.5px] text-slate-600 dark:text-slate-300 mt-0.5 line-clamp-2">
                          &ldquo;{a.remark}&rdquo;
                        </div>
                      ) : null}
                    </div>
                    <div className="text-right shrink-0">
                      <Badge className={`text-[10px] ${
                        a.status === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                        a.status === "rejected" ? "bg-rose-100 text-rose-700 border-rose-200" :
                        "bg-amber-100 text-amber-800 border-amber-200"
                      }`}>
                        {a.status}
                      </Badge>
                      {at ? (
                        <div className="text-[9.5px] text-slate-500 mt-0.5">{at}</div>
                      ) : (
                        a.status === "pending" ? <div className="text-[9.5px] text-slate-400 mt-0.5 italic">Awaiting</div> : null
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="app-details-close">Close</Button>
          <Button onClick={onReject} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="app-details-reject">
            <XCircle size={13} className="mr-1.5" /> Reject
          </Button>
          <Button onClick={onApprove} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="app-details-approve">
            <CheckCircle2 size={13} className="mr-1.5" /> Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
